import { useState } from 'react'
import { Calendar, Users, Plus, Edit2, Trash2, Eye } from 'lucide-react'
import adminApi from '../../../services/adminApi'
import AdminEventModal from '../modals/AdminEventModal'
import EventRegistrationsModal from '../modals/EventRegistrationsModal'

const EventsTab = ({ events, spaces, facilitators, onRefresh, onEnsureSpacesAndFacilitators }) => {
  // Event modal state
  const [showEventModal, setShowEventModal] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)

  // Registrations modal state
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false)
  const [selectedEventForRegistrations, setSelectedEventForRegistrations] = useState(null)
  const [eventRegistrations, setEventRegistrations] = useState([])
  const [registrationsLoading, setRegistrationsLoading] = useState(false)

  const handleOpenEventModal = async () => {
    setEditingEvent(null)
    if (onEnsureSpacesAndFacilitators) await onEnsureSpacesAndFacilitators()
    setShowEventModal(true)
  }

  const handleEditEvent = async (event) => {
    setEditingEvent(event)
    if (onEnsureSpacesAndFacilitators) await onEnsureSpacesAndFacilitators()
    setShowEventModal(true)
  }

  const handleCloseEventModal = () => {
    setShowEventModal(false)
    setEditingEvent(null)
  }

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      await adminApi.delete(`/api/admin/events/${eventId}`)
      onRefresh(true)
    } catch (err) {
      console.error('Error deleting event:', err)
      alert(err.response?.data?.message || 'Failed to delete event')
    }
  }

  const handleViewEventRegistrations = async (event) => {
    setSelectedEventForRegistrations(event)
    setRegistrationsLoading(true)
    setShowRegistrationsModal(true)

    try {
      const res = await adminApi.get(`/api/admin/events/${event.id}/registrations`)
      setEventRegistrations(res.data.data)
    } catch (err) {
      console.error('Error fetching registrations:', err)
    } finally {
      setRegistrationsLoading(false)
    }
  }

  if (!events) return null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Events ({events.length})
        </h3>
        <button
          onClick={handleOpenEventModal}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No events created yet</p>
          <button
            onClick={handleOpenEventModal}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create your first event
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10">
            <thead>
              <tr>
                {['Name', 'Date', 'Format', 'Status', 'Facilitators', 'Registrations', 'Actions'].map(header => (
                  <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-white/5">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-white">{event.name}</div>
                    <div className="text-xs text-gray-400">{event.slug}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      {new Date(event.eventDate).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(event.eventDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.format === 'in-person' ? 'bg-emerald-500/20 text-emerald-300' :
                      event.format === 'online' ? 'bg-blue-500/20 text-blue-300' :
                      'bg-purple-500/20 text-purple-300'
                    }`}>
                      {event.format}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.status === 'published' ? 'bg-green-500/20 text-green-300' :
                      event.status === 'draft' ? 'bg-gray-500/20 text-gray-300' :
                      event.status === 'cancelled' ? 'bg-red-500/20 text-red-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`}>
                      {event.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {event.facilitators?.slice(0, 2).map(fac => (
                        <span key={fac.id} className="text-xs text-gray-300 bg-white/10 px-2 py-0.5 rounded">
                          {fac.name}
                        </span>
                      ))}
                      {event.facilitators?.length > 2 && (
                        <span className="text-xs text-gray-400">+{event.facilitators.length - 2}</span>
                      )}
                      {event.externalFacilitators?.slice(0, 1).map((name, i) => (
                        <span key={i} className="text-xs text-amber-300 bg-amber-500/20 px-2 py-0.5 rounded">
                          {name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="flex items-center gap-1 text-sm text-gray-300">
                      <Users className="w-4 h-4" />
                      {event.registrationCount}
                      {event.maxAttendees && `/${event.maxAttendees}`}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditEvent(event)}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleViewEventRegistrations(event)}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                        title="View Registrations"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminEventModal
        show={showEventModal}
        onClose={handleCloseEventModal}
        editingEvent={editingEvent}
        spaces={spaces}
        facilitators={facilitators}
        onSaved={() => onRefresh(true)}
      />

      <EventRegistrationsModal
        show={showRegistrationsModal}
        onClose={() => setShowRegistrationsModal(false)}
        event={selectedEventForRegistrations}
        registrations={eventRegistrations}
        loading={registrationsLoading}
      />
    </div>
  )
}

export default EventsTab
